import type { Transfer, Warehouse } from "@estelle/domain";

import type { InTransitLine } from "@/modules/warehouses/in-transit";

/** A row flattened to the field names this module declares. */
export type Projection = Record<string, unknown>;

/**
 * A warehouse flattened for display.
 *
 * Computed from the row alone — the same restriction `projectOrder`
 * documents and for the same reason: this is what `DataTable` calls to
 * render a cell, and it must not reach past the record `list()` already
 * returned. `city` and `state` come straight off the mirrored `address`,
 * never from a second lookup.
 */
export function projectWarehouse(warehouse: Warehouse): Projection {
  return {
    name: warehouse.name,
    code: warehouse.code,
    city: warehouse.address?.city ?? "",
    state: warehouse.address?.province ?? "",
    isActive: warehouse.isActive,
    fulfilsOrders: warehouse.fulfilsOrders,
  };
}

/**
 * A transfer with the two warehouse names its row needs to display.
 *
 * `Transfer` itself only carries `fromWarehouseId` / `toWarehouseId` — a
 * mirrored id is not something anyone reads at a glance. `TransfersList`
 * builds this by joining a page of transfers against `warehouses.list()`,
 * both calls the sanctioned interface already makes available; `projectTransfer`
 * below still only reads the row it is handed, it does not go fetch
 * anything itself.
 */
export interface TransferRow extends Transfer {
  fromWarehouseName: string;
  toWarehouseName: string;
}

export function projectTransfer(transfer: TransferRow): Projection {
  return {
    reference: transfer.reference,
    fromWarehouse: transfer.fromWarehouseName,
    toWarehouse: transfer.toWarehouseName,
    status: transfer.status,
    createdAt: transfer.createdAt,
    dispatchedAt: transfer.dispatchedAt,
  };
}

/**
 * One in-transit line, flattened for display.
 *
 * `InTransitLine` is already the joined, computed row `deriveInTransitLines`
 * built — this only renames it to the field names `IN_TRANSIT_FIELDS`
 * declares, the same job `projectOrder` does for an order.
 */
export function projectInTransitLine(line: InTransitLine): Projection {
  return {
    reference: line.reference,
    fromWarehouse: line.fromWarehouseName,
    toWarehouse: line.toWarehouseName,
    quantity: line.quantity,
    dispatchedAt: line.dispatchedAt,
    ageDays: line.ageDays,
  };
}
